/*
 * hero-math: パラメトリック・モーフィング点群ヒーロー
 * 数式(球面調和関数/リサージュ/ローズ曲線/波の重ね合わせ)から毎フレーム点群を計算し、
 * 数式とパラメータの現在値をリアルタイム表示する。
 * three.js は可視化検知後に dynamic import(URLは liquid から data-three-src で注入)。
 */
(() => {
  if (customElements.get('hero-math')) return;

  const gauss = () => (Math.random() + Math.random() + Math.random() - 1.5) / 1.5;

  // 各形状: 時間変化するパラメータ + 数式表示 + 点群生成
  function buildShapeDefs(N, R1, R2, R3, JX, JY, JZ) {
    return {
      orbital: {
        params: (T) => ({ l: 2 + 0.7 * Math.sin(T * 0.06), m: 2 + 0.7 * Math.sin(T * 0.042 + 2.0) }),
        eq: (p) => `r(θ, φ) = | sin(l·θ) · cos(m·φ) |,   l = ${p.l.toFixed(2)},  m = ${p.m.toFixed(2)}`,
        fill(out, p) {
          for (let i = 0; i < N; i++) {
            const th = Math.acos(2 * R1[i] - 1);
            const ph = R2[i] * Math.PI * 2;
            const lobe = Math.pow(Math.abs(Math.sin(p.l * th) * Math.cos(p.m * ph)), 0.5);
            const r = (0.25 + 1.15 * lobe) * (0.85 + R3[i] * 0.3);
            out[i * 3] = r * Math.sin(th) * Math.cos(ph) + JX[i] * 0.05;
            out[i * 3 + 1] = r * Math.cos(th) + JY[i] * 0.05;
            out[i * 3 + 2] = r * Math.sin(th) * Math.sin(ph) + JZ[i] * 0.05;
          }
        },
      },
      lissajous: {
        params: (T) => ({ d: (T * 0.12) % (Math.PI * 2) }),
        eq: (p) => `x = sin(3t + δ),  y = sin(4t),  z = sin(5t),   δ = ${p.d.toFixed(2)}`,
        fill(out, p) {
          for (let i = 0; i < N; i++) {
            const t = R1[i] * Math.PI * 2;
            out[i * 3] = 1.35 * Math.sin(3 * t + p.d) + JX[i] * 0.045;
            out[i * 3 + 1] = 1.35 * Math.sin(4 * t) + JY[i] * 0.045;
            out[i * 3 + 2] = 0.8 * Math.sin(5 * t + p.d * 0.5) + JZ[i] * 0.045;
          }
        },
      },
      rose: {
        params: (T) => ({ k: 3.5 + 1.0 * Math.sin(T * 0.05) }),
        eq: (p) => `r(θ) = sin(k·θ),   k = ${p.k.toFixed(2)}`,
        fill(out, p) {
          for (let i = 0; i < N; i++) {
            const t = R1[i] * Math.PI * 2;
            const envelope = Math.pow(Math.abs(Math.sin((p.k * t) / 2)), 0.55);
            const rho = Math.pow(R2[i], 0.42) * envelope * 1.5;
            const curl = 0.42 * rho * Math.sin(2.6 * rho + t * 2.0);
            out[i * 3] = rho * Math.cos(t) + JX[i] * 0.035;
            out[i * 3 + 1] = rho * Math.sin(t) + JY[i] * 0.035;
            out[i * 3 + 2] = curl + JZ[i] * 0.077;
          }
        },
      },
      wave: {
        params: (T) => ({ ph: (T * 0.28) % (Math.PI * 2) }),
        eq: (p) => `y = Σ Aᵢ sin(fᵢ·x + φ),   φ = ${p.ph.toFixed(2)}`,
        fill(out, p) {
          for (let i = 0; i < N; i++) {
            const x = (R1[i] - 0.5) * 3.4;
            const z = (R2[i] - 0.5) * 2.2;
            const y =
              0.28 * Math.sin(2.4 * x + 1.6 * z + p.ph) +
              0.18 * Math.sin(4.2 * x - 2.0 * z + p.ph * 1.7) +
              0.1 * Math.sin(7.0 * x + p.ph * 0.6);
            out[i * 3] = x + JX[i] * 0.03;
            out[i * 3 + 1] = y + JY[i] * 0.03;
            out[i * 3 + 2] = z + JZ[i] * 0.03;
          }
        },
      },
    };
  }

  const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  class HeroMath extends HTMLElement {
    connectedCallback() {
      if (this.initStarted) return;

      // ガード: フォールバック画像のままにする条件
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (navigator.connection && navigator.connection.saveData) return;
      if (this.hasAttribute('data-disable-mobile') && window.innerWidth < 750) return;

      this.io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !this.initStarted) {
            this.initStarted = true;
            this.init();
          }
          this.visible = e.isIntersecting;
          if (this.ready) this.toggleLoop();
        }
      });
      this.io.observe(this);
    }

    disconnectedCallback() {
      this.dispose();
    }

    async init() {
      let THREE;
      try {
        THREE = await import(this.dataset.threeSrc);
      } catch (e) {
        return; // ロード失敗 → フォールバック画像のまま
      }

      const canvas = this.querySelector('.hero-math__canvas');
      let renderer;
      try {
        renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
      } catch (e) {
        return; // WebGL不可 → フォールバック画像のまま
      }
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      this.renderer = renderer;

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
      camera.position.set(0, 0.15, 4.4);

      // 粒子数(モバイルは約半分)
      const base = parseInt(this.dataset.particles, 10) || 24000;
      const isMobile = window.innerWidth < 750;
      const N = isMobile ? Math.round(base * 0.45) : base;

      // 粒子ごとの乱数を固定(パラメータ変化で点がなめらかに動くように)
      const R1 = new Float32Array(N), R2 = new Float32Array(N), R3 = new Float32Array(N);
      const JX = new Float32Array(N), JY = new Float32Array(N), JZ = new Float32Array(N);
      for (let i = 0; i < N; i++) {
        R1[i] = Math.random(); R2[i] = Math.random(); R3[i] = Math.random();
        JX[i] = gauss(); JY[i] = gauss(); JZ[i] = gauss();
      }

      const defs = buildShapeDefs(N, R1, R2, R3, JX, JY, JZ);
      const names = (this.dataset.shapes || 'orbital,lissajous,rose,wave')
        .split(',').map((s) => s.trim()).filter((s) => defs[s]);
      const shapes = (names.length ? names : ['orbital']).map((n) => defs[n]);

      const pos = new Float32Array(N * 3);
      const tmpA = new Float32Array(N * 3);
      const tmpB = new Float32Array(N * 3);
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      const style = getComputedStyle(this);
      const ink = style.getPropertyValue('--color-foreground') || '26,26,26';
      // モバイルは粒子数を減らすぶん、サイズと濃さを上げて視認性を保つ
      const baseSize = isMobile ? 0.024 : 0.016;
      const mat = new THREE.PointsMaterial({
        color: `rgb(${ink.trim()})`,
        size: baseSize,
        transparent: true,
        opacity: isMobile ? 0.65 : 0.5,
        depthWrite: false,
        sizeAttenuation: true,
      });
      const cloud = new THREE.Points(geo, mat);
      cloud.rotation.x = -0.35;
      scene.add(cloud);
      this.geo = geo;
      this.mat = mat;

      const eqFormula = this.querySelector('.hero-math__eq-formula');
      const eqBox = this.querySelector('.hero-math__eq');

      const speed = parseFloat(this.dataset.speed) || 1;
      const HOLD = parseFloat(this.dataset.hold) || 10;
      const MORPH = 4.5;
      let shapeIdx = 0, phaseT = 0, morphing = false;

      const updateCloud = (dt, T) => {
        phaseT += dt;
        const cur = shapes[shapeIdx];
        const nxt = shapes[(shapeIdx + 1) % shapes.length];
        cur.fill(tmpA, cur.params(T));

        let e = 0;
        if (!morphing || shapes.length < 2) {
          if (phaseT >= HOLD && shapes.length > 1) { morphing = true; phaseT = 0; }
          pos.set(tmpA);
        } else {
          const k = Math.min(phaseT / MORPH, 1);
          e = easeInOut(k);
          nxt.fill(tmpB, nxt.params(T));
          for (let i = 0; i < N * 3; i++) pos[i] = tmpA[i] + (tmpB[i] - tmpA[i]) * e;
          if (k >= 1) { shapeIdx = (shapeIdx + 1) % shapes.length; morphing = false; phaseT = 0; }
        }
        geo.attributes.position.needsUpdate = true;

        if (eqFormula) {
          const s = morphing && e > 0.5 ? nxt : cur;
          eqFormula.textContent = s.eq(s.params(T));
          eqBox.style.opacity = morphing ? String(0.25 + 0.75 * Math.abs(1 - 2 * e)) : '1';
        }
      };

      // マウスパララックス + カーソル斥力場用のNDC座標
      let mx = 0, my = 0;
      let pxN = 0, pyN = 0, pointerOn = false;
      this.onPointerMove = (e) => {
        mx = e.clientX / window.innerWidth - 0.5;
        my = e.clientY / window.innerHeight - 0.5;
        const rect = canvas.getBoundingClientRect();
        pointerOn =
          e.clientX >= rect.left && e.clientX <= rect.right &&
          e.clientY >= rect.top && e.clientY <= rect.bottom;
        if (pointerOn) {
          pxN = ((e.clientX - rect.left) / rect.width) * 2 - 1;
          pyN = -((e.clientY - rect.top) / rect.height) * 2 + 1;
        }
      };
      window.addEventListener('pointermove', this.onPointerMove);
      // タッチは指を離したら斥力を止める
      this.onPointerEnd = (e) => {
        if (e.pointerType !== 'mouse') pointerOn = false;
      };
      window.addEventListener('pointerup', this.onPointerEnd);
      window.addEventListener('pointercancel', this.onPointerEnd);

      // タップパルス: 広がりながら消える斥力の波紋(モバイルの主要インタラクション)
      let pulses = [];
      this.onPointerDown = (e) => {
        const rect = canvas.getBoundingClientRect();
        if (
          e.clientX < rect.left || e.clientX > rect.right ||
          e.clientY < rect.top || e.clientY > rect.bottom
        ) return;
        pulses.push({
          nx: ((e.clientX - rect.left) / rect.width) * 2 - 1,
          ny: -((e.clientY - rect.top) / rect.height) * 2 + 1,
          t0: performance.now() / 1000,
        });
        if (pulses.length > 3) pulses.shift();
      };
      window.addEventListener('pointerdown', this.onPointerDown);

      // NDC座標 → 点群ローカル座標(z=0平面に投影)
      const repelWorld = new THREE.Vector3();
      const ndcToLocal = (nx, ny) => {
        repelWorld.set(nx, ny, 0.5).unproject(camera);
        repelWorld.sub(camera.position).normalize();
        const t = -camera.position.z / repelWorld.z;
        if (!(t > 0)) return null;
        repelWorld.multiplyScalar(t).add(camera.position);
        return cloud.worldToLocal(repelWorld);
      };

      // 中心 L・半径 R・強さ S の斥力場を粒子に適用
      const applyField = (L, R, S) => {
        const R2 = R * R;
        for (let i = 0; i < N; i++) {
          const i3 = i * 3;
          const dx = pos[i3] - L.x, dy = pos[i3 + 1] - L.y, dz = pos[i3 + 2] - L.z;
          const r2 = dx * dx + dy * dy + dz * dz;
          if (r2 < R2 && r2 > 1e-6) {
            const r = Math.sqrt(r2);
            const f = ((R - r) / R) * S; // 中心ほど強く、縁でなめらかにゼロ
            pos[i3] += (dx / r) * f;
            pos[i3 + 1] += (dy / r) * f;
            pos[i3 + 2] += (dz / r) * f;
          }
        }
        geo.attributes.position.needsUpdate = true;
      };

      const applyRepulsion = () => {
        cloud.updateMatrixWorld();
        // カーソル(ホバー中)の定常斥力
        if (pointerOn) {
          const L = ndcToLocal(pxN, pyN);
          if (L) applyField(L, 0.6, 0.4);
        }
        // タップパルス: 半径が広がりながら強さが減衰(約1秒)
        const nowS = performance.now() / 1000;
        pulses = pulses.filter((p) => nowS - p.t0 < 1.0);
        for (const p of pulses) {
          const age = nowS - p.t0;
          const L = ndcToLocal(p.nx, p.ny);
          if (!L) continue;
          const R = 0.3 + age * 1.4; // 波紋として広がる
          const S = 0.5 * (1 - age); // 減衰
          applyField(L, R, S);
        }
      };

      this.onResize = () => {
        const w = canvas.clientWidth, h = canvas.clientHeight;
        renderer.setSize(w, h, false);
        camera.aspect = w / h;
        // 縦長画面では点群全体が収まるようカメラを引く
        camera.position.z = camera.aspect < 1 ? Math.min(4.4 / camera.aspect, 7.5) : 4.4;
        camera.updateProjectionMatrix();
        // 背の低いバンド表示では点が小さくなりすぎるため拡大する
        // ただしモバイルのバンド表示(Work/Shopヒーロー)では大きくなりすぎるため 0.7 を掛ける
        const heightScale = Math.min(Math.max(560 / Math.max(h, 1), 1), 2.2);
        const bandMobile = isMobile && !this.classList.contains('hero-math--full') ? 0.7 : 1;
        mat.size = baseSize * heightScale * bandMobile;
      };
      window.addEventListener('resize', this.onResize);
      this.onResize();

      let t0 = performance.now();
      this.tick = (now) => {
        this.rafId = null;
        const dt = Math.min((now - t0) / 1000, 0.1) * speed;
        t0 = now;
        updateCloud(dt, (now / 1000) * speed);
        applyRepulsion();
        cloud.rotation.z += dt * 0.03;
        cloud.rotation.x = -0.35 + my * 0.25;
        cloud.rotation.y = mx * 0.35;
        cloud.scale.setScalar(1 + Math.sin(now / 4000) * 0.02);
        renderer.render(scene, camera);
        if (this.visible) this.rafId = requestAnimationFrame(this.tick);
      };

      this.ready = true;
      this.classList.add('hero-math--ready');
      this.toggleLoop();
    }

    // 画面外では rAF を停止して負荷を抑える
    toggleLoop() {
      if (this.visible && this.rafId == null && this.tick) {
        this.rafId = requestAnimationFrame((now) => {
          // 停止していた時間で dt が跳ねないように基準を取り直す
          this.tick(now);
        });
      } else if (!this.visible && this.rafId != null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }

    dispose() {
      if (this.io) this.io.disconnect();
      if (this.rafId != null) cancelAnimationFrame(this.rafId);
      if (this.onPointerMove) window.removeEventListener('pointermove', this.onPointerMove);
      if (this.onPointerEnd) {
        window.removeEventListener('pointerup', this.onPointerEnd);
        window.removeEventListener('pointercancel', this.onPointerEnd);
      }
      if (this.onPointerDown) window.removeEventListener('pointerdown', this.onPointerDown);
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.geo) this.geo.dispose();
      if (this.mat) this.mat.dispose();
      if (this.renderer) this.renderer.dispose();
      this.io = null; this.rafId = null; this.tick = null; this.ready = false; this.initStarted = false;
    }
  }

  customElements.define('hero-math', HeroMath);

  // テーマエディタ: セクション再読み込み時に破棄(要素ごと差し替わるため disconnectedCallback でも走る)
  document.addEventListener('shopify:section:unload', (e) => {
    const el = e.target.querySelector && e.target.querySelector('hero-math');
    if (el && el.dispose) el.dispose();
  });
})();
